<script setup>
import { router, Link } from "@inertiajs/vue3";
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue'
import UserMenuLarge from "./UserMenuLarge.vue";
import LogoSvg from '@svg/new-logo.svg'
import SearchSvg from '@svg/new-search.svg'
import DashboardSvg from '@svg/new-dashboard.svg'
import ProfileSvg from '@svg/new-profile.svg'
import SignOutSvg from '@svg/new-signout.svg'
import NotificationSvg from '@svg/new-notification.svg'
import HamburgerSvg from '@svg/new-hamburger.svg'
import useCloudflareImage from '@/Composables/useCloudflareImage.js'
import Notifications from '../../../Components/New/Notifications/Notifications.vue'
import { useNotificationsStore } from '@/Stores/notifications'

const notificationsStore = useNotificationsStore()
const { cloudflareImage } = useCloudflareImage()
</script>

<template>
    <nav class="py-6 mb-4 xl:mb-8 h-auto xl:h-36 flex items-center justify-between flex-wrap">
        <Link :href="`/`" class="block" aria-label="Go to homepage">
            <LogoSvg class="w-[176px] h-[31px]" />
        </Link>
        <div class="hidden xl:flex grow ml-8 items-center relative before:absolute before:w-[1.5px] before:h-5 before:bg-[rgba(53,67,116,1)] before:left-0 pl-6">
            <Link :href="`/library`" class="text-[rgba(235,248,255,1)] text-sm p-3">Courses</Link>
            <Link :href="`/topics`" class="text-[rgba(235,248,255,1)] text-sm p-3">Topics</Link>
            <Link :href="`/paths`" class="text-[rgba(235,248,255,1)] text-sm p-3">Paths</Link>
            <Link :href="`/pro`" class="text-[rgba(235,248,255,1)] text-sm p-3">Premium</Link>
        </div>
        <div class="flex items-center">
            <Link dusk="navigation-search" preserve-state :href="`/search`" class="border-[1.5px] border-[rgba(53,67,116,1)] px-2 py-2 xl:px-3.5 xl:py-2.5 rounded-full text-[rgba(132,156,241,1)] text-sm flex items-center space-x-2.5 leading-none" aria-label="Search">
                <SearchSvg class="size-5 min-w-5" />
                <span class="hidden xl:inline pr-2 xl:pr-12" id="searchLabel">Search the library</span>
            </Link>

            <!-- mobile navigation -->
            <Menu class="xl:hidden ml-5 -mr-5 z-50" as="div" v-slot="{ open }">
                <transition
                    enter-active-class="transition ease-out"
                    enter-from-class="transform opacity-0 -translate-y-2"
                    leave-active-class="transition ease-out"
                    leave-to-class="transform opacity-0 -translate-y-2"
                >
                    <MenuItems class="absolute top-0 left-0 w-full bg-[rgba(14,20,53,1)] rounded-bl-xl rounded-br-xl p-5 ui-focus-visible:ring-2 ui-focus-visible:ring-offset-2 focus:outline-none">
                        <div class="p-2">
                            <div class="space-x-5" v-if="!$page.props.auth.user">
                                <Link :href="`/auth/register`" class="bg-[#41FBDC] rounded-full px-3 py-1.5 text-[rgba(20, 22, 57, 1)] text-sm drop-shadow-[0px_2px_6px_rgba(26,188,151,0.5)]">
                                    Get started
                                </Link>

                                <Link :href="`/auth/signin`" preserve-state :data="{ from: $page.url }" class="text-[rgba(235,248,255,1)] text-sm">
                                    Sign in
                                </Link>
                            </div>

                            <div v-if="$page.props.auth.user" class="flex items-center">
                                <img :src="cloudflareImage($page.props.auth.user.avatar)" class="lg:opacity-100 transition-opacity rounded-full h-10 w-10 ring-offset-2 ring-2 ring-offset-[rgba(8,13,39,1)] mr-5" :class="{ 'ring-[rgba(65,251,220,1)]': $page.props.auth.user.subscription.active, 'ring-[rgba(255,207,83,1)]': !$page.props.auth.user.subscription.active, 'opacity-0': !open }" alt="Profile photo">

                                <div class="space-y-2 lg:opacity-100 transition-opacity" :class="{ 'opacity-0': !open }">
                                    <div class="text-base text-[rgba(235,248,255,1)] leading-none font-medium lg:max-w-[120px] truncate">
                                        {{ $page.props.auth.user.first_name }}
                                    </div>
                                    <div class="inline-block rounded-full border-[1.5px] text-xs leading-none px-2.5 py-1" :class="{ 'border-[rgba(65,251,220,1)] text-[rgba(65,251,220,1)] shadow-[2px_2px_8px_rgba(26,188,151,0.3)]': $page.props.auth.user.subscription.active, 'border-[rgba(255,207,83,1)] text-[rgba(255,207,83,1)] shadow-[2px_2px_8px_rgba(255,207,83,0.3)]': !$page.props.auth.user.subscription.active }">
                                        {{ $page.props.auth.user.subscription.active ? ($page.props.auth.user.subscription.lifetime ? 'Lifetime' : 'Premium') : `Basic` }}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="space-y-2.5 pt-6">
                            <MenuItem v-slot="{ close }">
                                <Link :href="`/library`" v-on:success="close" class="bg-[rgba(25,27,67,1)] text-[rgba(132,156,241,1)] text-sm px-3.5 py-2 rounded-lg flex items-center">
                                    <span>Courses</span>
                                </Link>
                            </MenuItem>
                            <MenuItem v-slot="{ close }">
                                <Link :href="`/topics`" v-on:success="close" class="bg-[rgba(25,27,67,1)] text-[rgba(132,156,241,1)] text-sm px-3.5 py-2 rounded-lg flex items-center">
                                    <span>Topics</span>
                                </Link>
                            </MenuItem>
                            <MenuItem v-slot="{ close }">
                                <Link :href="`/paths`" v-on:success="close" class="bg-[rgba(25,27,67,1)] text-[rgba(132,156,241,1)] text-sm px-3.5 py-2 rounded-lg flex items-center">
                                    <span>Paths</span>
                                </Link>
                            </MenuItem>
                            <MenuItem v-slot="{ close }">
                                <Link :href="`/forum`" v-on:success="close" class="bg-[rgba(25,27,67,1)] text-[rgba(132,156,241,1)] text-sm px-3.5 py-2 rounded-lg flex items-center">
                                    <span>Forum</span>
                                </Link>
                            </MenuItem>

                            <div v-if="$page.props.auth.user" class="space-y-2.5 border-t-[1.5px] border-[rgba(25,27,67,1)] pt-3 !mt-3">
                                <MenuItem v-slot="{ close }">
                                    <Link :href="`/user/activity`" v-on:success="close" class="bg-[rgba(25,27,67,1)] text-[rgba(132,156,241,1)] text-sm px-3.5 py-2 rounded-lg flex items-center space-x-2.5">
                                        <DashboardSvg class="size-5" />
                                        <span>Dashboard</span>
                                    </Link>
                                </MenuItem>
                                <MenuItem v-slot="{ close }">
                                    <Link :href="`/user/profile`" v-on:success="close" class="bg-[rgba(25,27,67,1)] text-[rgba(132,156,241,1)] text-sm px-3.5 py-2 rounded-lg flex items-center space-x-2.5">
                                        <ProfileSvg class="size-5" />
                                        <span>Profile</span>
                                    </Link>
                                </MenuItem>
                                <MenuItem>
                                    <button v-on:click="router.post('/logout')" class="w-full">
                                        <div class="bg-[rgba(25,27,67,1)] text-[rgba(132,156,241,1)] text-sm px-3.5 py-2 rounded-lg flex items-center space-x-2.5">
                                            <SignOutSvg class="size-5" />
                                            <span>Sign out</span>
                                        </div>
                                    </button>
                                </MenuItem>
                            </div>
                        </div>
                    </MenuItems>
                </transition>

                <!-- drop-shadow-none is a hack to get negative z-index to be overlayed and clickable. do not remove it -->
                <div class="flex items-center drop-shadow-none">
                    <MenuButton class="mr-6 flex items-center ui-focus-visible:ring-2 ui-focus-visible:ring-offset-2 focus:outline-none" aria-label="Menu">
                        <img v-if="$page.props.auth.user" :src="cloudflareImage($page.props.auth.user.avatar)" class="lg:opacity-100 transition-opacity rounded-full h-7 w-7 ring-offset-2 ring-2 ring-offset-[rgba(8,13,39,1)] mr-5" :class="{ 'ring-[rgba(65,251,220,1)]': $page.props.auth.user.subscription.active, 'ring-[rgba(255,207,83,1)]': !$page.props.auth.user.subscription.active, 'opacity-0': open }" alt="Profile photo">
                        <HamburgerSvg class="w-5 h-5 stroke-[rgba(132,156,241,1)]" />
                    </MenuButton>
                </div>
            </Menu>

            <div class="ml-8 hidden xl:block" v-if="$page.props.auth.user">
                <button
                    v-on:click="notificationsStore.panelOpen = true"
                    class="relative border-[1.5px] border-[rgba(53,67,116,1)] px-2 py-2 rounded-full text-[rgba(132,156,241,1)] text-sm flex items-center space-x-2.5 leading-none"
                >
                    <NotificationSvg class="size-5 min-w-5" />
                    <div
                        v-if="notificationsStore.unreadNotificationsCount"
                        class="absolute size-5 text-[0.7rem] bg-[rgba(35,41,89,1)] shadow text-[rgba(235,248,255,1)] flex items-center justify-center rounded-full -top-2 -right-2 font-semibold"
                    >
                        {{ notificationsStore.unreadNotificationsCount }}
                    </div>
                </button>
            </div>

            <!-- desktop user navigation -->
            <UserMenuLarge class="hidden xl:block" />

            <!-- unauthenticated desktop navigation -->
            <div class="ml-8 space-x-4 hidden xl:block" v-if="!$page.props.auth.user">
                <Link :href="`/auth/signin`" preserve-state :data="{ from: $page.url }" class="text-[rgba(235,248,255,1)] text-sm p-3">
                    Sign in
                </Link>

                <Link :href="`/auth/register`" class="bg-[#41FBDC] rounded-full px-3 py-1.5 text-[rgba(20, 22, 57, 1)] text-sm drop-shadow-[0px_2px_6px_rgba(26,188,151,0.5)]">
                    Get started
                </Link>
            </div>
        </div>
    </nav>

    <Notifications v-if="$page.props.auth.user" />
</template>
